import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, AlertTriangle, CheckCircle2, Loader2, Mail, RefreshCw, Send, Zap } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-black/60 p-4" data-testid={`outbound-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

const STATUS_VARIANTS = {
  sent: "border-emerald-500/40 text-emerald-300",
  delivered: "border-emerald-500/40 text-emerald-300",
  opened: "border-sky-500/40 text-sky-300",
  clicked: "border-violet-500/40 text-violet-300",
  bounced: "border-rose-500/40 text-rose-300",
  dropped: "border-rose-500/40 text-rose-300",
  spam_report: "border-rose-500/40 text-rose-300",
  errored: "border-rose-500/40 text-rose-300",
  queued: "border-amber-500/40 text-amber-300",
  deferred: "border-amber-500/40 text-amber-300",
  dry_run: "border-zinc-600 text-zinc-400",
};

function TestSendDialog({ templates, onSent }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ to: "matthew@blackrdg.com", template_slug: "member_magic_link", context: "" });

  const submit = async () => {
    if (!form.to) { toast.error("Recipient required"); return; }
    let ctx = {};
    if (form.context.trim()) {
      try { ctx = JSON.parse(form.context); }
      catch (_e) { toast.error("Context must be valid JSON"); return; }
    }
    setBusy(true);
    try {
      const result = await crmApi.sendTestEmail({
        to: form.to,
        template_slug: form.template_slug || null,
        context: ctx,
      });
      if (result.status === "sent") {
        toast.success(`Sent! Provider ID: ${result.provider_message_id || "(none)"}`);
      } else if (result.status === "dry_run") {
        toast(`Dry-run (SendGrid not configured). Message logged with status=dry_run.`);
      } else {
        toast.error(`Status: ${result.status}. ${result.error_message || ""}`);
      }
      onSent?.();
      setOpen(false);
    } catch (exc) {
      toast.error(exc.message || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400" data-testid="open-test-send">
          <Send className="h-4 w-4 mr-1" /> Send test email
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white max-w-lg">
        <DialogHeader><DialogTitle>Send test email</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Recipient</Label>
            <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="test-to" placeholder="someone@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Template</Label>
            <Select value={form.template_slug} onValueChange={(v) => setForm({ ...form, template_slug: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="test-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Context (JSON)</Label>
            <textarea
              rows={5}
              value={form.context}
              onChange={(e) => setForm({ ...form, context: e.target.value })}
              className="w-full rounded-md bg-zinc-950 border border-zinc-700 text-white font-mono text-xs p-2"
              placeholder='{"customer_name":"Matthew","portal_url":"https://blackrdg.com/members/login"}'
              data-testid="test-context"
            />
            <p className="text-[11px] text-zinc-500">Variables in the template like <span className="font-mono">{`{{ customer_name }}`}</span> are filled from this JSON.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-300" disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="confirm-test-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OutboundView() {
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, msgs, tpls] = await Promise.all([
          crmApi.outboundSummary(),
          crmApi.listEmailMessages({ limit: 50 }),
          crmApi.listTemplates({ status: "active" }),
        ]);
        if (!active) return;
        setSummary(s);
        setMessages(msgs || []);
        setTemplates(tpls || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load outbound dashboard");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [refreshCounter]);

  const reload = () => setRefreshCounter((c) => c + 1);

  const runTick = async () => {
    try {
      await crmApi.triggerNurtureTick();
      toast.success("Worker tick ran");
      reload();
    } catch (exc) {
      toast.error(exc.message || "Tick failed");
    }
  };

  if (loading || !summary) {
    return (
      <div className="text-sm text-zinc-400 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading outbound…
      </div>
    );
  }

  const kpi = summary.kpi || {};
  const sg = summary.sendgrid || {};

  return (
    <div className="space-y-6" data-testid="owner-outbound">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Mail className="h-5 w-5 text-amber-300" /> Outbound</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Jordan-powered email engine. Templates, sequences, and live delivery tracking via SendGrid.
            {summary.twilio_configured ? null : <span className="text-amber-300"> SMS will activate once Twilio + A2P 10DLC are approved.</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" onClick={reload} data-testid="outbound-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" onClick={runTick} data-testid="outbound-tick">
            <Zap className="h-4 w-4 mr-1" /> Run worker tick
          </Button>
          <TestSendDialog templates={templates} onSent={reload} />
        </div>
      </div>

      <Card className="bg-black/40 border-zinc-800" data-testid="outbound-sendgrid-status">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center gap-2"><Activity className="h-4 w-4 text-amber-300" /> Provider status</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">SendGrid</div>
            <div className={`mt-1 flex items-center gap-1 ${sg.configured ? "text-emerald-300" : "text-amber-300"}`}>
              {sg.configured ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {sg.configured ? "configured" : "not configured"}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 break-all">key: {sg.key_prefix || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">From</div>
            <div className="mt-1 text-white">{sg.from_email || "—"}</div>
            <div className="text-[11px] text-zinc-500">{sg.from_name || ""}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">Webhook verification</div>
            <div className={`mt-1 ${sg.webhook_verification ? "text-emerald-300" : "text-amber-300"}`}>
              {sg.webhook_verification ? "signed" : "unsigned (events still recorded)"}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">Twilio (SMS)</div>
            <div className="mt-1 text-zinc-400">{summary.twilio_configured ? "configured" : "awaiting A2P + creds"}</div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="outbound-kpis">
        <Stat label="Sent today" value={kpi.today_sent || 0} accent="text-emerald-300" />
        <Stat label="Delivered today" value={kpi.today_delivered || 0} />
        <Stat label="Errored today" value={kpi.today_errored || 0} accent={kpi.today_errored ? "text-rose-300" : undefined} />
        <Stat label="Active enrollments" value={kpi.active_enrollments || 0} accent="text-amber-300" />
        <Stat label="Week total" value={kpi.week_total || 0} />
        <Stat label="Week opens" value={kpi.week_opened || 0} accent="text-sky-300" />
        <Stat label="Week clicks" value={kpi.week_clicked || 0} accent="text-violet-300" />
        <Stat label="Week bounces" value={kpi.week_bounced || 0} accent={kpi.week_bounced ? "text-rose-300" : undefined} />
      </div>

      <Card className="bg-black/40 border-zinc-800" data-testid="outbound-log-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white">Recent messages</CardTitle>
        </CardHeader>
        <CardContent>
          {messages.length === 0 ? (
            <p className="text-sm text-zinc-400 py-6 text-center">No outbound sends yet. Trigger a test send above.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="outbound-log-table">
                <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-800">
                  <tr>
                    <th className="text-left py-2 px-3">When</th>
                    <th className="text-left py-2 px-3">To</th>
                    <th className="text-left py-2 px-3">Subject</th>
                    <th className="text-left py-2 px-3">Template</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Opens / Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => (
                    <tr key={m.id} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`message-row-${m.id}`}>
                      <td className="py-2 px-3 text-zinc-400 text-[11px]">{new Date(m.created_at).toLocaleString()}</td>
                      <td className="py-2 px-3 text-white">{m.recipient_email}</td>
                      <td className="py-2 px-3 text-zinc-300">{m.subject || "(no subject)"}</td>
                      <td className="py-2 px-3 text-zinc-400 text-[11px]">{m.template_slug || m.purpose}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className={STATUS_VARIANTS[m.status] || "border-zinc-700 text-zinc-300"}>
                          {m.status}
                        </Badge>
                        {m.error_message && <div className="text-[10px] text-rose-300 mt-1 line-clamp-1">{m.error_message}</div>}
                      </td>
                      <td className="py-2 px-3 text-zinc-400">{m.open_count || 0} / {m.click_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
